import React from 'react'
import { classToFComponent, FCClassContext, Field, FieldHelp, FieldLabel, inputSx } from 'src/utils/views'
import { Logger } from 'wdc-cube'

import DeleteIcon from '@mui/icons-material/Delete'

import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'

import { Divider } from '@mui/material'
import {
    FilterAccordion,
    FilterAccordionDetails,
    FilterAccordionSummary
} from 'src/components/filteraccordion/filter_accordion'
import { DateFilterScope, DateParcelFilterScope, JunctionMode } from '../../tcm_scopes'
import { DateOperator } from '../../tcm_service'
import { FieldIconButton, FilterPanel, criterionLabel } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('DateFilterView')

type DateFilterViewProps = {
    className?: string
    scope: DateFilterScope
}

class DateFilterViewClass implements FCClassContext<DateFilterViewProps> {
    // :: Fields
    scope!: DateFilterScope
    setAnchorEl!: React.Dispatch<React.SetStateAction<HTMLElement | null>>

    onSyncState({ scope }: DateFilterViewProps) {
        this.scope = scope
    }

    render({ className }: DateFilterViewProps) {
        const { scope } = this

        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
        this.setAnchorEl = setAnchorEl

        const menuAndOrOpened = !!anchorEl

        return (
            <FilterAccordion className={className} expanded={scope.expanded} onChange={this.handleExpandedChange}>
                <FilterAccordionSummary
                    onClear={this.handleClearAllClicked}
                    onAdd={this.handlePlusClick}
                    caption={scope.caption}
                    captionBold={!!scope.conjunction.length || !!scope.disjunction.length}
                />
                <FilterAccordionDetails>
                    {!!scope.conjunction.length &&
                        scope.conjunction.map((parcelScope, idx, arr) => (
                            <DateKeywordParcelView
                                key={parcelScope.uid}
                                acceptBlankValues={scope.acceptBlankValues}
                                scope={parcelScope}
                                divider={idx < arr.length - 1 || !!scope.disjunction.length}
                            />
                        ))}

                    {!!scope.disjunction.length &&
                        scope.disjunction.map((parcelScope, idx, arr) => (
                            <DateKeywordParcelView
                                key={parcelScope.uid}
                                acceptBlankValues={scope.acceptBlankValues}
                                scope={parcelScope}
                                divider={idx < arr.length - 1}
                            />
                        ))}

                    {!scope.conjunction.length && !scope.disjunction.length && (
                        <FieldHelp>Nenhum critério adicionado. Use o + para incluir um.</FieldHelp>
                    )}
                </FilterAccordionDetails>
            </FilterAccordion>
        )
    }

    readonly handleExpandedChange = (_event: React.SyntheticEvent, expanded: boolean) => {
        this.scope.onExpandedChange(expanded).catch(LOG.caught)
    }

    readonly handlePlusClick = () => {
        //this.setAnchorEl(event.currentTarget)
        this.handleAddORClicked()
    }

    readonly handleClose = () => {
        this.setAnchorEl(null)
    }

    readonly handleAddANDClicked = () => {
        this.setAnchorEl(null)
        this.scope.onAddFilter(JunctionMode.AND).catch(LOG.caught)
    }

    readonly handleAddORClicked = () => {
        this.setAnchorEl(null)
        this.scope.onAddFilter(JunctionMode.OR).catch(LOG.caught)
    }

    readonly handleClearAllClicked = () => {
        this.setAnchorEl(null)
        this.scope.onClearAllFilters().catch(LOG.caught)
    }
}

export const DateKeywordFilterView = classToFComponent(DateFilterViewClass, React)
export default DateKeywordFilterView

// :: StringParcelViewClass

type DateParcelViewProps = {
    className?: string
    acceptBlankValues: boolean
    scope: DateParcelFilterScope
    divider?: boolean
}

class DateKeywordParcelViewClass implements FCClassContext<DateParcelViewProps> {
    scope!: DateParcelFilterScope

    render({ className, scope, divider }: DateParcelViewProps) {
        this.scope = scope

        return (
            <>
                <FilterPanel className={className}>
                    <Field>
                        <FieldLabel htmlFor={scope.uid}>{criterionLabel(false)}</FieldLabel>
                        <TextField
                            id={scope.uid}
                            select
                            size="small"
                            defaultValue={`${DateOperator.in_range}`}
                            sx={inputSx}
                            fullWidth
                        >
                            <MenuItem value={DateOperator.in_range}>No intervalo</MenuItem>
                        </TextField>
                    </Field>
                    {scope.valueVisible && (
                        <>
                            <Field>
                                <FieldLabel htmlFor={`${scope.uid}-de`}>De</FieldLabel>
                                <TextField
                                    id={`${scope.uid}-de`}
                                    size="small"
                                    type="date"
                                    value={scope.value}
                                    onChange={this.handleFromValueChange}
                                    onKeyDown={this.handleKeyDown}
                                    sx={inputSx}
                                    fullWidth
                                    slotProps={{
                                        inputLabel: { shrink: true }
                                    }}
                                />
                            </Field>
                            <Field>
                                <FieldLabel htmlFor={`${scope.uid}-ate`}>Até</FieldLabel>
                                <TextField
                                    id={`${scope.uid}-ate`}
                                    size="small"
                                    type="date"
                                    value={scope.toValue}
                                    onChange={this.handleToValueChange}
                                    onKeyDown={this.handleKeyDown}
                                    sx={inputSx}
                                    fullWidth
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <FieldIconButton
                                                    size="small"
                                                    aria-label="Limpar intervalo"
                                                    onClick={this.handleClearClick}
                                                >
                                                    <DeleteIcon />
                                                </FieldIconButton>
                                            )
                                        },

                                        inputLabel: { shrink: true }
                                    }}
                                />
                                <FieldHelp>O intervalo inclui as duas datas informadas.</FieldHelp>
                            </Field>
                        </>
                    )}
                </FilterPanel>
                {divider && <Divider sx={{ borderColor: 'var(--border)' }} />}
            </>
        )
    }

    readonly handleFromValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onValueChange(event.target.value, 'from').catch(LOG.caught)
    }

    readonly handleToValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onValueChange(event.target.value, 'to').catch(LOG.caught)
    }

    readonly handleClearClick = () => {
        this.scope.onClear().catch(LOG.caught)
    }

    readonly handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            this.scope.onEnterKeyPressed().catch(LOG.caught)
        }
    }
}

export const DateKeywordParcelView = classToFComponent(DateKeywordParcelViewClass, React)
